/**
 * Vista 1 · Acceso.
 *
 * `SEG-4`: el mensaje de error es GENÉRICO y no revela si falló el correo o la
 * contraseña. Esa decisión está tomada en el servidor —que además compara un
 * hash aunque la cuenta no exista, para no delatarlo por el tiempo de
 * respuesta— y aquí basta con no mejorarla por nuestra cuenta.
 */
import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Input, PasswordInput } from '@mpfn/ui'
import { AuthCard, Callout, FormField } from '@mpfn/patterns'
import { BrandLockup } from '@mpfn/brand'
import { api, ErrorApi } from '../api/cliente.js'
import type { RespuestaSesion } from '../api/tipos.js'

export function Acceso({ onIniciada }: { onIniciada: () => Promise<void> }) {
  const idCorreo = useId()
  const idPassword = useId()
  const [correo, setCorreo] = useState('')
  const [password, setPassword] = useState('')

  const iniciar = useMutation({
    mutationFn: (credenciales: { correo: string; password: string }) =>
      api.crear<RespuestaSesion>('/auth/sesion', credenciales),
    onSuccess: () => onIniciada(),
  })

  const mensaje =
    iniciar.error instanceof ErrorApi
      ? iniciar.error.message
      : iniciar.error
        ? 'No fue posible comunicarse con el servidor. Vuelva a intentarlo.'
        : undefined

  return (
    <AuthCard
      aside={
        // `tone="onLight"` y no `onBrand`: el `AuthCard` del design system
        // monta su superficie sobre `--mpfn-background`, que es claro. El
        // boceto del inventario dibujaba el aside en azul, pero pintarlo sería
        // reimplementar el layout del DS (`UI-5`) y dejaría el logotipo
        // negativo —blanco— invisible. El azul de marca lo lleva el sidebar de
        // la aplicación, que sí es la superficie de marca.
        <BrandLockup
          tone="onLight"
          size="lg"
          orientation="stacked"
          name="SIGFISC"
          description="Fiscalización de contrataciones"
        />
      }
    >
      <form
        className="sigfisc-formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          iniciar.mutate({ correo: correo.trim(), password })
        }}
      >
        <h1 className="sigfisc-formulario__titulo">Ingresar al sistema</h1>

        {mensaje !== undefined && (
          <Callout tone="critical" title="No se pudo iniciar sesión">
            {mensaje}
          </Callout>
        )}

        <FormField id={idCorreo} label="Correo institucional" required>
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            value={correo}
            onChange={(evento) => {
              setCorreo(evento.target.value)
            }}
          />
        </FormField>

        <FormField id={idPassword} label="Contraseña" required>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(evento) => {
              setPassword(evento.target.value)
            }}
          />
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={iniciar.isPending}
          disabled={correo.trim() === '' || password === ''}
        >
          Ingresar
        </Button>
      </form>
    </AuthCard>
  )
}
